// Portfolio process progress: turning one row per (project, process instance) into the counts a
// dashboard cell draws, and into the drill-through a step click fires (REQ-013, REQ-014).
//
// Framework-free on purpose. Everything worth arguing about — which bucket a project falls into,
// what counts as stalled, what a step click asks for — is here, where it can be tested without a
// dashboard, a DOM or a server.
//
// ── Where the numbers come from ────────────────────────────────────────────────────────────────────
// The server returns STORED state: where each project has got to, and how long since anything
// happened on its instance. It deliberately does not decide whether that is stalled. The same
// portfolio is stalled at thirty days and healthy at ninety, so the threshold is a property of how
// this cell is being read and lives on its config — in one place, rather than half here and half in
// a filter, which is how a server and a browser end up disagreeing about one word.

/** One row as the provider returns it. Property names are PascalCase — the wire keeps C# casing. */
export interface ProcessPortfolioRow {
    ProjectId: number;
    ProjectNumber?: string | null;
    ProjectName?: string | null;
    InstanceUid?: string | null;
    ProcessKey?: string | null;
    ProcessDisplayName?: string | null;
    InstanceState?: string | null;
    StepKey?: string | null;
    StepDisplayName?: string | null;
    StepDisplayOrder?: number | null;
    CompletedSteps?: number | null;
    TotalSteps?: number | null;
    SatisfiedMandatoryItems?: number | null;
    MandatoryItems?: number | null;
    LastActivityAt?: string | null;
    DaysSinceLastActivity?: number | null;
}

export interface ProcessPortfolioConfig {
    Title?: string | null;
    StalledAfterDays?: number | null;
    ShowCompleted?: boolean | null;
    DrillThroughGridTypeKey?: string | null;
}

/** One line of the widget: a step (or a terminal state) and the projects sitting there. */
export interface ProcessPortfolioBucket {
    /** Stable within a render. A step's own key, or one of the two terminal sentinels below. */
    key: string;
    /** What the line is called. */
    label: string;
    /** The process the step belongs to, when the portfolio spans more than one. */
    processLabel?: string;
    /** Distinguishes a real step from the completed / stopped lines. */
    kind: "step" | "completed" | "stopped";
    order: number;
    projectCount: number;
    stalledCount: number;
    /** Project ids in this bucket, deduplicated — what the drill-through asks the grid for. */
    projectIds: number[];
    /** The stalled subset, in the same order. */
    stalledProjectIds: number[];
}

export const CompletedBucketKey = "__completed";
export const StoppedBucketKey = "__stopped";

/** Terminal buckets sort after every step; a stopped procedure sorts after a finished one. */
const CompletedOrder = 1_000_000;
const StoppedOrder = 1_000_001;

export const DefaultStalledAfterDays = 30;

/**
 * A project sitting in a bucket is stalled when nothing has been recorded on its instance for longer
 * than the configured number of days.
 *
 * ⚠️ Read the boundary carefully: a row with no `DaysSinceLastActivity` at all is NOT stalled. The
 * absence of a figure means the provider could not date the instance, and reporting "we do not know"
 * as "nothing has happened for months" would put a project on a management report on the strength of
 * a missing value.
 */
export function isStalled(row: ProcessPortfolioRow, stalledAfterDays: number): boolean {
    const days = row?.DaysSinceLastActivity;
    if (days == null || !Number.isFinite(days)) return false;
    if (!Number.isFinite(stalledAfterDays) || stalledAfterDays <= 0) return false;
    return days > stalledAfterDays;
}

/** The threshold this cell reads its data at, falling back to the shared default. */
export function stalledAfterDays(config: ProcessPortfolioConfig | null | undefined): number {
    const configured = config?.StalledAfterDays;
    return typeof configured === "number" && Number.isFinite(configured) && configured > 0
        ? configured
        : DefaultStalledAfterDays;
}

/**
 * Groups the rows into the lines the widget draws.
 *
 * A row with a current step goes under that step. One without goes under `completed` or `stopped`
 * according to the instance's own state — never under a step it is not at, and never silently
 * dropped, because a portfolio that quietly omitted every finished project would flatter itself.
 * An in-progress instance with no current step at all (every step still blocked) has nowhere honest
 * to go and is left out of the step counts; it is reported separately as `unplaced`.
 */
export function buildPortfolio(
    rows: readonly ProcessPortfolioRow[] | null | undefined,
    config?: ProcessPortfolioConfig | null,
): { buckets: ProcessPortfolioBucket[]; totalProjects: number; totalRows: number; unplaced: number } {
    const threshold = stalledAfterDays(config);
    const showCompleted = config?.ShowCompleted !== false;

    const byKey = new Map<string, ProcessPortfolioBucket>();
    const projects = new Set<number>();
    let unplaced = 0;
    let totalRows = 0;

    for (const row of rows ?? []) {
        if (!row) continue;
        totalRows++;
        projects.add(row.ProjectId);

        const state = (row.InstanceState ?? "").toLowerCase();
        let key: string;
        let label: string;
        let kind: ProcessPortfolioBucket["kind"];
        let order: number;

        if (row.StepKey) {
            key = `${row.ProcessKey ?? ""}::${row.StepKey}`;
            label = row.StepDisplayName || row.StepKey;
            kind = "step";
            order = row.StepDisplayOrder ?? 0;
        } else if (state === "stopped") {
            key = StoppedBucketKey;
            label = "Stopped";
            kind = "stopped";
            order = StoppedOrder;
        } else if (state === "completed") {
            if (!showCompleted) continue;
            key = CompletedBucketKey;
            label = "Completed";
            kind = "completed";
            order = CompletedOrder;
        } else {
            // In progress but sitting at no step the engine calls current. Nothing truthful can be
            // said about where it is, so it is counted and reported rather than placed.
            unplaced++;
            continue;
        }

        let bucket = byKey.get(key);
        if (!bucket) {
            bucket = {
                key,
                label,
                processLabel: kind === "step"
                    ? (row.ProcessDisplayName || row.ProcessKey || undefined)
                    : undefined,
                kind,
                order,
                projectCount: 0,
                stalledCount: 0,
                projectIds: [],
                stalledProjectIds: [],
            };
            byKey.set(key, bucket);
        }

        if (!bucket.projectIds.includes(row.ProjectId)) {
            bucket.projectIds.push(row.ProjectId);
            bucket.projectCount++;
        }

        if (isStalled(row, threshold) && !bucket.stalledProjectIds.includes(row.ProjectId)) {
            bucket.stalledProjectIds.push(row.ProjectId);
            bucket.stalledCount++;
        }
    }

    const buckets = [...byKey.values()].sort((a, b) =>
        a.order - b.order || a.label.localeCompare(b.label));

    return { buckets, totalProjects: projects.size, totalRows, unplaced };
}

/** What the widget draws when there is nothing to draw. */
export function emptyStateText(totalRows: number): string {
    return totalRows === 0
        ? "No projects are running a process."
        : "No project is sitting at a step.";
}

/** The shape a step click hands to the host — the `openGrid` half of a click intent. */
export interface PortfolioDrillIntent {
    openGrid: {
        gridTypeKey: string;
        filterSource: "override";
        customFilter: unknown;
        title: string;
        suppressNewButton: true;
    };
}

/**
 * The drill-through for one bucket: the projects sitting there, by id (REQ-014).
 *
 * ⚠️ The filter is a **canonical Serenity criteria tuple** — `[[Field], op, value]` — with the ids
 * already substituted, not a `${token}` for the host to resolve. The widget knows the ids at the
 * moment of the click; handing over a token would make the drill-through depend on the host's scope
 * carrying an array it has no reason to have.
 *
 * Returns undefined when there is nothing to open: no grid configured, or no projects in the bucket.
 * A drill-through that opened an empty grid would read as "there are none", which is a different
 * statement from "you cannot go there".
 */
export function buildDrillIntent(
    bucket: ProcessPortfolioBucket | null | undefined,
    config?: ProcessPortfolioConfig | null,
): PortfolioDrillIntent | undefined {
    const gridTypeKey = (config?.DrillThroughGridTypeKey ?? "").trim();
    if (!gridTypeKey || !bucket || bucket.projectIds.length === 0) return undefined;

    return {
        openGrid: {
            gridTypeKey,
            filterSource: "override",
            customFilter: [["Id"], "in", [[...bucket.projectIds]]],
            title: bucket.processLabel
                ? `${bucket.processLabel} — ${bucket.label}`
                : bucket.label,
            suppressNewButton: true,
        },
    };
}
